import { ReactNode, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Button,
  ButtonProps,
} from "@mui/material";
import ConfirmationModal from "./misc/ConfirmationModal";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import LoadingIndicator from "./misc/LoadingIndicator";
import ActionTooltip from "./misc/ActionTooltip";
import SvgIcon, { SvgIconType } from "../helpers/SvgIcon";
import { ActionMenuComponentContext } from "./actionMenu/ActionMenuComponentContext";

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: "1.075rem",
    maxWidth: 500,
  },
});
interface CustomProps {
  icon?: SvgIconType;
  navigate?: string;
  withDefaultAnchor?: boolean;
  tooltipTitle?: string;
  tooltipArrow?: boolean;
  tooltipPosition?: TooltipProps["placement"];
  loading?: boolean;
  loadingSize?: number;
  withConfirmationModal?: {
    modalTitle?: string;
    modalSubtitle?: string;
    modalLeftButtonText?: string;
    modalRightButtonText?: string;
    modalLeftButtonOnClick?: () => void;
    modalRightButtonOnClick?: () => void;
    modalLeftButtonColor?:
      | "error"
      | "info"
      | "inherit"
      | "primary"
      | "secondary"
      | "success";
    modalRightButtonColor?:
      | "error"
      | "info"
      | "inherit"
      | "primary"
      | "secondary"
      | "success";
    modalLeftButtonVariant?: "contained" | "outlined" | "text";
    modalRightButtonVariant?: "contained" | "outlined" | "text";
    modalSize?: "sm" | "lg" | "xl";
  };
  withDropdown?: {
    menuItems: {
      value: string | number;
      label: string | number;
      onClick: () => void;
    }[];
    anchorOrigin?: {
      vertical?: "top" | "bottom" | "center";
      horizontal?: "right" | "left" | "center";
    };
  };
}

const CustomButton = (props: ButtonProps & CustomProps) => {
  const actionMenuContext = useContext(ActionMenuComponentContext);

  useEffect(() => {
    actionMenuContext?.register(props);
  }, [actionMenuContext, props]);

  useEffect(() => {
    return () => actionMenuContext?.register(null);
  }, [actionMenuContext]);

  // The menu renders the trigger; keep the owning modal component mounted here.
  if (actionMenuContext) return null;

  const buttonProps = {
    ...props,
    color: props.color ?? (props.variant === "action" ? "inherit" : undefined),
    "aria-busy": props.loading || props["aria-busy"] || undefined,
  };
  delete buttonProps.navigate;
  delete buttonProps.withDefaultAnchor;
  delete buttonProps.tooltipTitle;
  delete buttonProps.tooltipArrow;
  delete buttonProps.tooltipPosition;
  delete buttonProps.loading;
  delete buttonProps.loadingSize;
  delete buttonProps.icon;
  delete buttonProps.withConfirmationModal;
  delete buttonProps.withDropdown;

  return (
    <LinkWrapper navigate={!buttonProps.disabled ? props.navigate : null}>
      <ChildrenWrapper
        buttonProps={{ ...buttonProps, disabled: !!buttonProps.disabled }}
        props={props}
      />
    </LinkWrapper>
  );
};

const ChildrenWrapper = ({
  buttonProps,
  props,
}: {
  buttonProps: any;
  props: CustomProps;
}) => {
  if (props.withConfirmationModal) {
    return <ConfirmationModalWrapper props={props} buttonProps={buttonProps} />;
  }

  if (props.withDropdown) {
    return <DropdownWrapper props={props} buttonProps={buttonProps} />;
  }

  return (
    <TooltipWrapper
      arrow={props.tooltipArrow ?? true}
      title={props.tooltipTitle}
      action={buttonProps.variant === "action"}
      focusable={buttonProps.disabled}
      fullWidth={buttonProps.fullWidth}
      placement={props.tooltipPosition}>
      <Button {...buttonProps} onClick={buttonProps.onClick}>
        <ButtonContent
          icon={props.icon}
          loading={props.loading}
          loadingSize={props.loadingSize}>
          {buttonProps.children}
        </ButtonContent>
      </Button>
    </TooltipWrapper>
  );
};

const ButtonContent = ({
  children,
  loading,
  loadingSize,
  icon,
}: {
  children: ReactNode;
  loading?: boolean;
  loadingSize?: number;
  icon?: SvgIconType;
}) => (
  <span
    className="relative inline-flex items-center justify-center"
    style={{ flexDirection: "inherit", gap: "inherit" }}
    aria-busy={loading || undefined}>
    <span
      className="inline-flex items-center justify-center"
      style={{
        flexDirection: "inherit",
        gap: "inherit",
        opacity: loading ? 0 : 1,
      }}>
      {icon && (
        <span
          aria-hidden="true"
          className={`${
            children ? "mr-2" : ""
          } inline-flex h-5 w-5 shrink-0 items-center justify-center [&_svg]:!h-5 [&_svg]:!w-5`}>
          <SvgIcon type={icon} fill="currentColor" />
        </span>
      )}
      {children}
    </span>
    {loading && (
      <span
        className="absolute inset-0 flex items-center justify-center"
        aria-hidden="true">
        <LoadingIndicator size={loadingSize ?? "1.25em"} color="inherit" />
      </span>
    )}
  </span>
);

const DropdownWrapper = ({
  buttonProps,
  props,
}: {
  buttonProps: any;
  props: CustomProps;
}) => {
  const { withDropdown } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TooltipWrapper
        arrow={props.tooltipArrow ?? true}
        title={props.tooltipTitle}
        action={buttonProps.variant === "action"}
        focusable={buttonProps.disabled}
        fullWidth={buttonProps.fullWidth}
        disabled={isMenuOpen}
        placement={props.tooltipPosition}>
        <Button {...buttonProps} onClick={handleMenuOpen}>
          <ButtonContent
            icon={props.icon}
            loading={props.loading}
            loadingSize={props.loadingSize}>
            {buttonProps.children}
          </ButtonContent>
        </Button>
      </TooltipWrapper>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: withDropdown?.anchorOrigin?.vertical ?? "top",
          horizontal: withDropdown?.anchorOrigin?.horizontal ?? "right",
        }}
        transformOrigin={{
          vertical: withDropdown?.anchorOrigin?.vertical ?? "top",
          horizontal: withDropdown?.anchorOrigin?.horizontal ?? "right",
        }}
        open={isMenuOpen}
        onClose={handleMenuClose}>
        {withDropdown?.menuItems.map((menuItem) => (
          <MenuItem
            key={menuItem.value}
            onClick={() => {
              handleMenuClose();
              // setTimeout is used to avoid two modals overlapping
              setTimeout(() => {
                menuItem.onClick();
              }, 400);
            }}>
            {menuItem.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ConfirmationModalWrapper = ({
  buttonProps,
  props,
}: {
  buttonProps: any;
  props: CustomProps;
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleClose = () => {
    setShowConfirmationModal(false);
  };

  const handleButtonClick = () => {
    setShowConfirmationModal(true);
  };

  if (!props.withConfirmationModal) {
    return <></>;
  }

  return (
    <>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleClose}
        title={props.withConfirmationModal.modalTitle}
        subtitle={props.withConfirmationModal.modalSubtitle}
        leftButtonText={props.withConfirmationModal.modalLeftButtonText}
        leftButtonOnClick={props.withConfirmationModal.modalLeftButtonOnClick}
        rightButtonText={props.withConfirmationModal.modalRightButtonText}
        rightButtonOnClick={props.withConfirmationModal.modalRightButtonOnClick}
        leftButtonColor={props.withConfirmationModal.modalLeftButtonColor}
        rightButtonColor={props.withConfirmationModal.modalRightButtonColor}
        leftButtonVariant={props.withConfirmationModal.modalLeftButtonVariant}
        rightButtonVariant={props.withConfirmationModal.modalRightButtonVariant}
        modalSize={props.withConfirmationModal.modalSize}
      />
      <TooltipWrapper
        arrow={props.tooltipArrow ?? true}
        title={props.tooltipTitle}
        action={buttonProps.variant === "action"}
        focusable={buttonProps.disabled}
        fullWidth={buttonProps.fullWidth}
        disabled={showConfirmationModal}
        placement={props.tooltipPosition}>
        <Button {...buttonProps} onClick={handleButtonClick}>
          <ButtonContent
            icon={props.icon}
            loading={props.loading}
            loadingSize={props.loadingSize}>
            {buttonProps.children}
          </ButtonContent>
        </Button>
      </TooltipWrapper>
    </>
  );
};

const LinkWrapper = ({
  navigate,
  children,
}: {
  navigate?: string | null;
  children: any;
}) => {
  if (!navigate) {
    return children;
  }

  return (
    <Link to={navigate} className="text-white no-underline">
      {children}
    </Link>
  );
};

const TooltipWrapper = ({
  children,
  title,
  arrow,
  placement = "top-start",
  disabled,
  action,
  focusable,
  fullWidth,
}: {
  children: any;
  title?: string | null;
  arrow?: boolean;
  placement?: TooltipProps["placement"];
  disabled?: boolean;
  action?: boolean;
  focusable?: boolean;
  fullWidth?: boolean;
}) => {
  if (!title) return children;
  const TooltipComponent = action ? ActionTooltip : CustomWidthTooltip;
  return (
    <TooltipComponent
      arrow={arrow}
      title={disabled ? "" : title}
      placement={placement}
      enterDelay={action ? 350 : undefined}
      enterNextDelay={action ? 0 : undefined}
      componentsProps={
        action ? { tooltip: { sx: { width: 300, minHeight: 88 } } } : undefined
      }
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, 8] },
          },
        ],
      }}>
      <span
        className={`inline-flex max-w-full ${fullWidth ? "w-full" : ""}`}
        tabIndex={focusable && !disabled ? 0 : undefined}
        aria-disabled={focusable || undefined}>
        {children}
      </span>
    </TooltipComponent>
  );
};

export default CustomButton;
